import { useState, useEffect } from "react";
import { format } from "date-fns";
import { toast } from "react-toastify";
import Spinner from "../../components/spinner";
import PAYMENT_REQUESTS from "../../api/services/PAYMENT_REQUESTS";
import styles from "../../styles/_containers/_paymentRequests/paymentRequestDetail.module.scss";

const STATUS_LABELS = {
  PENDING: "ממתין",
  PAID: "שולם",
  EXPIRED: "פג תוקף",
  CANCELLED: "בוטל",
};

const getStatusClass = (status) => {
  switch (status) {
    case "PENDING": return styles.statusPending;
    case "PAID": return styles.statusPaid;
    case "EXPIRED": return styles.statusExpired;
    case "CANCELLED": return styles.statusCancelled;
    default: return "";
  }
};

const formatDate = (date) => {
  if (!date) return null;
  try {
    return format(new Date(date), "dd/MM/yyyy HH:mm");
  } catch {
    return null;
  }
};

const PaymentRequestDetail = ({ request, onClose, onAction }) => {
  const [actionLoading, setActionLoading] = useState(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const customerName = request.customerName || request.customer?.name || "-";
  const customerPhone = request.customerPhone || request.customer?.phone || "";
  const serviceName = request.serviceName || request.service?.name || "-";

  // ── Build timeline events ──
  const timeline = [];
  if (request.createdAt) {
    timeline.push({ label: "נוצר", date: formatDate(request.createdAt) });
  }
  if (request.sentAt) {
    timeline.push({ label: "נשלח", date: formatDate(request.sentAt) });
  }
  if (request.viewedAt) {
    timeline.push({ label: "נצפה", date: formatDate(request.viewedAt) });
  }
  if (request.paidAt) {
    timeline.push({ label: "שולם", date: formatDate(request.paidAt) });
  }
  if (request.cancelledAt) {
    timeline.push({ label: "בוטל", date: formatDate(request.cancelledAt) });
  }

  // ── Resend handler ──
  const handleResend = async (channel) => {
    setActionLoading("resend_" + channel);
    try {
      await PAYMENT_REQUESTS.resend(request._id, channel);
      toast.success("הבקשה נשלחה מחדש בהצלחה");
      if (onAction) onAction();
    } catch (error) {
      toast.error(error?.response?.data?.message || "שגיאה בשליחה מחדש");
    }
    setActionLoading(null);
  };

  // ── Cancel handler ──
  const handleCancel = async () => {
    setActionLoading("cancel");
    try {
      await PAYMENT_REQUESTS.cancel(request._id);
      toast.success("הבקשה בוטלה בהצלחה");
      if (onAction) onAction();
    } catch (error) {
      toast.error(error?.response?.data?.message || "שגיאה בביטול הבקשה");
    }
    setActionLoading(null);
  };

  return (
    <>
      {/* Backdrop */}
      <div className={styles.overlay} onClick={onClose} />

      {/* Panel */}
      <div className={styles.panel}>
        {/* Header */}
        <div className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>{customerName}</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            &#10005;
          </button>
        </div>

        {/* Body */}
        <div className={styles.panelBody}>
          {/* Info section */}
          <div className={styles.infoSection}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>סכום</span>
              <span className={styles.infoValue}>
                {"\u20AA"}{request.amount}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>שירות</span>
              <span className={styles.infoValue}>{serviceName}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>תאריך יצירה</span>
              <span className={styles.infoValue}>
                {formatDate(request.createdAt) || "-"}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>סטטוס</span>
              <span className={`${styles.statusBadge} ${getStatusClass(request.status)}`}>
                {STATUS_LABELS[request.status] || request.status}
              </span>
            </div>
            {customerPhone && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>טלפון</span>
                <span className={styles.infoValue}>{customerPhone}</span>
              </div>
            )}
          </div>

          {/* Timeline */}
          {timeline.length > 0 && (
            <div className={styles.timelineSection}>
              <h3 className={styles.sectionTitle}>היסטוריה</h3>
              <div className={styles.timeline}>
                {timeline.map((event, idx) => (
                  <div
                    key={idx}
                    className={`${styles.timelineItem} ${idx === timeline.length - 1 ? styles.timelineItemLast : ""}`}
                  >
                    <div className={styles.timelineDot} />
                    <div className={styles.timelineContent}>
                      <span className={styles.timelineLabel}>{event.label}</span>
                      <span className={styles.timelineDate}>{event.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer — actions only for PENDING */}
        {request.status === "PENDING" && (
          <div className={styles.panelFooter}>
            <button
              className={styles.whatsappBtn}
              onClick={() => handleResend("whatsapp")}
              disabled={actionLoading === "resend_whatsapp"}
            >
              {actionLoading === "resend_whatsapp" ? <Spinner /> : "שלח שוב בוואטסאפ"}
            </button>
            <button
              className={styles.smsBtn}
              onClick={() => handleResend("sms")}
              disabled={actionLoading === "resend_sms"}
            >
              {actionLoading === "resend_sms" ? <Spinner /> : "שלח שוב ב-SMS"}
            </button>
            <button
              className={styles.cancelRequestBtn}
              onClick={handleCancel}
              disabled={actionLoading === "cancel"}
            >
              {actionLoading === "cancel" ? <Spinner /> : "בטל בקשה"}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default PaymentRequestDetail;
